import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Page from "../../../Page";
import UserListToolbar from "./UserListToolbar";
import UserListHead from "./UserListHead";
import SearchNotFound from "../../../SearchNotFound";
import ApprovePostDocModalSecs from "../../../ApprovePostDocModalSecs";
import ApprovePhDModal2s from "../../../ApprovePhDModal2s";
import {
  getProgressReportApprovalMoreDetail,
  confirmProgressReportApprovalListMentor,
} from "../../../../core/service/progressReport.service";
import Comment from "../../../profile/Comment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";
import excelJs from "exceljs";
import {
  Card,
  Container,
  Grid,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Link,
} from "@mui/material";
import { config } from "../../../../core/service/config.service";

const TABLE_HEAD = [
  { id: "name_en", label: "Name", alignRight: false },
  { id: "report_for", label: "Report for", alignRight: false },
  { id: "created_date", label: "Date", alignRight: false },
  { id: "role_nm", label: "Program", alignRight: false },
];

const ROLE_ADMIN = "admin";
const ROLE_MENTOR = "mentor";
const ROLE_PHD3Y = "phd_3y";
const ROLE_PHD5Y = "phd_5y";
const ROLE_POSTDOC = "post_doc";

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const applySortFilter = (array, comparator, query) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return array.filter((e) => {
      const { title_en, name_en, middle_en, surname_en } = e;
      const name = `${title_en} ${name_en} ${middle_en || ""} ${surname_en}`;
      return name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
    });
  }
  return stabilizedThis.map((el) => el[0]);
};

ApproveResTable.propTypes = {
  listApprove: PropTypes.array,
};

export default function ApproveResTable({ listApprove }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openDetailPostDoc, setOpenDetailPostDoc] = useState(false);
  const [openDetailPhd, setOpenDetailPhd] = useState(false);
  const [moreDetailListApprovePostDoc, setMoreDetailListApprovePostDoc] =
    useState({});
  const [moreDetailListApprovePhd, setMoreDetailListApprovePhd] = useState({});
  const { user: currentUser } = useSelector((state) => state.authReducer);

  const openDetail = async (row) => {
    const { role_nm, report_id, form_nm } = row;
    if (role_nm === ROLE_POSTDOC) {
      const [moreDetail] = await getProgressReportApprovalMoreDetail(
        report_id,
        form_nm
      );
      setMoreDetailListApprovePostDoc(moreDetail);
      setOpenDetailPostDoc(true);
    } else {
      if (form_nm === "2c") {
        const [moreDetail] = await getProgressReportApprovalMoreDetail(
          report_id,
          form_nm
        );
        setMoreDetailListApprovePhd(moreDetail);
      } else {
        const moreDetail = await getProgressReportApprovalMoreDetail(
          report_id,
          form_nm
        );
        setMoreDetailListApprovePhd(moreDetail);
      }
      setOpenDetailPhd(true);
    }
  };

  const mentorConfirm = async (row, confirm) => {
    const { report_id } = row;
    await confirmProgressReportApprovalListMentor(report_id, confirm);
    closeMoreDetail();
  };

  const closeMoreDetail = () => {
    setMoreDetailListApprovePostDoc({});
    setMoreDetailListApprovePhd({});
    setOpenDetailPhd(false);
    setOpenDetailPostDoc(false);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - listApprove.length) : 0;

  const filteredUsers = applySortFilter(
    listApprove,
    getComparator(order, orderBy),
    filterName
  );

  const isUserNotFound = filteredUsers.length === 0;

  const exportExcel = async () => {
    const workbook = new excelJs.Workbook();
    const ws = workbook.addWorksheet(`Report`);
    ws.columns = [
      {
        header: "Name",
        key: "name_en",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Report for",
        key: "report_for",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Date",
        key: "created_date",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
      {
        header: "Program",
        key: "role_nm",
        width: 10,
        style: {
          alignment: { horizontal: "center", vertical: "middle" },
          font: { bold: true },
        },
      },
    ];

    ws.getRow(1).height = 30;

    await Promise.all(
      filteredUsers.map(async (val, index) => {
        ws.addRow([
          `${val.name_en} ${val.middle_en || ""} ${val.surname_en}`,
          val.semester
            ? val.semester === "semester_extension"
              ? "Semester Extension"
              : val.semester
            : val.report_for !== "2c" && val.report_for !== "2a"
            ? val.report_for
            : val.report_for !== "2c"
            ? "Fill PART 2A"
            : "Fill PART 2C",
          val.created_date,
          val.role_nm === ROLE_POSTDOC
            ? "Post Doc"
            : val.role_nm === ROLE_PHD3Y
            ? "PhD 3 year"
            : "PhD 5 year",
        ]);
        ws.getRow(index + 2).font = {
          ...ws.getRow(index + 2).font,
          bold: false,
        };
      })
    );

    workbook.xlsx
      .writeBuffer({
        base64: true,
      })
      .then((xls64) => {
        // build anchor tag and attach file (works in chrome)
        var a = document.createElement("a");
        var data = new Blob([xls64], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        var url = URL.createObjectURL(data);
        a.href = url;
        a.download = `PROGRESS_REPORT_APPROVAL_${moment().format(
          "YYYYMMDD_HHmmss"
        )}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(function () {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 0);
      })
      .catch(function (error) {
        console.log(error.message);
      });
  };
 
  

  return (
    <Page title="Progress Report Approval">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Progress Report Approval
          </Typography>
          <Link href={`${config.host_api}export/progress-report/${currentUser.id}`}>
            <Button variant="contained" >
              Export excel
            </Button>
          </Link>
        </Stack>

        <Card>
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={listApprove.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, i) => {
                    const {
                      role_nm,
                      title_en,
                      middle_en,
                      name_en,
                      surname_en,
                      created_date,
                      report_for,
                      semester,
                    } = row;

                    const name = `${title_en} ${name_en} ${
                      middle_en || ""
                    } ${surname_en}`;

                    return (
                      <TableRow tabIndex={-1} hover key={i}>
                        <TableCell />

                        <TableCell component="th" scope="row" padding="none">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>{name}</Typography>
                          </Stack>
                        </TableCell>

                        <TableCell width={"30%"} align="left">
                          {semester
                            ? semester
                            : report_for !== "2c" && report_for !== "2a"
                            ? report_for
                            : report_for !== "2c"
                            ? "Fill PART 2A"
                            : "Fill PART 2C"}
                        </TableCell>

                        <TableCell
                          sx={{ textTransform: "capitalize" }}
                          align="left"
                        >
                          {created_date}
                        </TableCell>

                        <TableCell align="left">
                          {role_nm === ROLE_POSTDOC
                            ? "Post Doc"
                            : role_nm === ROLE_PHD3Y
                            ? "PhD 3 year"
                            : "PhD 5 year"}
                        </TableCell>

                        <TableCell align="left">
                          <IconButton onClick={() => openDetail(row)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>

                        <Dialog
                          open={openDetailPhd}
                          onClose={closeMoreDetail}
                          fullWidth
                          maxWidth={"lg"}
                        >
                          <Stack alignItems="center">
                            <DialogTitle>Name (PhD)</DialogTitle>
                          </Stack>
                          <DialogContent>
                            <Grid container spacing={2}>
                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Program:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListApprovePhd.role_nm ||
                                          moreDetailListApprovePhd?.data
                                            ?.role_nm}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Email:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListApprovePhd.email ||
                                          moreDetailListApprovePhd?.data?.email}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>

                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Submit Date:
                                      </Typography>
                                      <Typography>
                                        {moment(
                                          moreDetailListApprovePhd.created_date
                                        ).format("DD/MM/YY") ||
                                          moment(
                                            moreDetailListApprovePhd?.data
                                              ?.created_date
                                          ).format("DD/MM/YY")}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Report for:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListApprovePhd.sending_scholarship_report ||
                                          moreDetailListApprovePhd?.data
                                            ?.sending_scholarship_report}
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>
                            </Grid>

                            <ApprovePhDModal2s
                              detail={moreDetailListApprovePhd}
                            />

                            <Comment
                              report={{
                                report_id:
                                  moreDetailListApprovePhd?.report_id ||
                                  moreDetailListApprovePhd?.data?.report_id,
                                user_id:
                                  moreDetailListApprovePhd?.mentor_id ||
                                  moreDetailListApprovePhd?.data?.mentor_id,
                              }}
                              sx={{ marginTop: 6 }}
                            />
                          </DialogContent>
                          {moreDetailListApprovePhd?.mentor_approve === 0 && (
                            <DialogActions>
                              <Button
                                onClick={() =>
                                  mentorConfirm(moreDetailListApprovePhd, 2)
                                }
                              >
                                Disapprove
                              </Button>
                              <Button
                                onClick={() =>
                                  mentorConfirm(moreDetailListApprovePhd, 1)
                                }
                              >
                                Approve
                              </Button>
                            </DialogActions>
                          )}
                        </Dialog>

                        <Dialog
                          open={openDetailPostDoc}
                          onClose={closeMoreDetail}
                          fullWidth
                          maxWidth={"lg"}
                        >
                          <Stack alignItems="center">
                            <DialogTitle>Name (Postdoc)</DialogTitle>
                          </Stack>
                          <DialogContent>
                            <Grid container spacing={2}>
                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Program:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListApprovePostDoc?.role_nm ===
                                          ROLE_POSTDOC && "Post Doc"}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Email:
                                      </Typography>
                                      <Typography>
                                        {moreDetailListApprovePostDoc?.email}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Report for:
                                      </Typography>
                                      <Typography>
                                        {
                                          moreDetailListApprovePostDoc?.sendingFellowshipReport
                                        }
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Progress achieved (%):
                                      </Typography>
                                      <Typography>
                                        {
                                          moreDetailListApprovePostDoc?.progressCommencementFellowship
                                        }
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>

                              <Grid item xs>
                                <List>
                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Submit Date:
                                      </Typography>
                                      <Typography>
                                        {moment(
                                          moreDetailListApprovePostDoc?.created_date
                                        ).format("DD/MM/YYYY")}
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Title:
                                      </Typography>
                                      <Typography>
                                        {
                                          moreDetailListApprovePostDoc?.researchTitleEnglish
                                        }
                                      </Typography>
                                    </Stack>
                                  </ListItem>

                                  <ListItem>
                                    <Stack direction="row" spacing={3}>
                                      <Typography variant="subtitle1">
                                        Main Research Area:
                                      </Typography>
                                      <Typography>
                                        {
                                          moreDetailListApprovePostDoc?.mainResearchArea
                                        }
                                      </Typography>
                                    </Stack>
                                  </ListItem>
                                </List>
                              </Grid>
                            </Grid>

                            <ApprovePostDocModalSecs
                              detail={moreDetailListApprovePostDoc}
                            />

                            <Comment
                              report={{
                                report_id:
                                  moreDetailListApprovePostDoc?.report_id,
                                user_id:
                                  moreDetailListApprovePostDoc?.mentor_id,
                              }}
                              sx={{ marginTop: 6 }}
                            />
                          </DialogContent>
                          {moreDetailListApprovePostDoc?.mentor_approve ===
                            0 && (
                            <DialogActions>
                              <Button
                                onClick={() =>
                                  mentorConfirm(moreDetailListApprovePostDoc, 2)
                                }
                              >
                                Disapprove
                              </Button>
                              <Button
                                onClick={() =>
                                  mentorConfirm(moreDetailListApprovePostDoc, 1)
                                }
                              >
                                Approve
                              </Button>
                            </DialogActions>
                          )}
                        </Dialog>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={listApprove.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
